/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Avatar, Card, CircularProgress, IconButton, Popover } from '@mui/material';
import moment from 'moment';
import AddCommentModal from 'pages/B2B/Jobs/AddComment/AddComment';
import React, { forwardRef, useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { toggleFavoriteCandidate } from 'services/jobPipeline';
import styled from 'styled-components';
import { ChatDotsFill } from 'styled-icons/bootstrap';
import { MoreVertical } from 'styled-icons/evaicons-solid';
import { DragIndicator, Person, Star } from 'styled-icons/material';
import { Candidate } from 'types/jobPipeline';
import { formatLabel } from 'utilities/utils';
import { useJobPipeline } from '../contexts/JobPipelineContext';
import JobCardMenuOptions from './JobCardMenuOptions';

interface JobCardProps {
    candidate: Candidate;
    index: number;
    onMenuAction: (action: string, id: string) => void;
    onFavoriteToggle: (id: string, favorite: boolean) => void;
}

const CardContainer = styled(Card)`
    margin-bottom: 8px;
    padding: 10px;
    border: 1px solid #E0E0E0;
    box-shadow: none;
    &:hover {
        box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    }
`;

const CandidateHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
`;

const CandidateNameSection = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const CandidateName = styled.div`
    display: flex;
    flex-direction: column;
    font-weight: 600;
    color: #333333;
    font-size: 12px;
`;

const LastUpdated = styled.span`
    font-weight: 400;
    color: #757575;
    font-size: 12px;
`;

const StatsSection = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 14px;
    font-weight: 500;
    justify-content: flex-end; 
    span {
        color: #333;
        font-size: 10px;
    }
    .tier {
        color: #2e7d32;
    }
    .ai {
        color: #f57c00;
    }
    .sme {
        color: #d32f2f;
    }
    .score {
        color: #fbc02d;
    }
    .completion {
        color: #0288d1;
    }
`;

const CandidateTags = styled.div`
    display: flex;
    gap: 4px;
    margin: 10px 0;
`;

const Tag = styled.span<{ background: string }>`
    padding: 2px 4px;
    border-radius: 12px;
    background-color: ${props => `${props.background}CC`};  
    font-size: 8px;
    font-weight: 600;
    color: black;
    opacity: 0.8;
`;

const ScheduledTag = styled.span`
    padding: 3px 4px;
    border-radius: 12px;
    background-color: #32DE84;
    font-size: 10px;
    font-weight: 600;
    color: black;
    opacity: 0.8;
`;

const ActionsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const DragIcon = styled(DragIndicator)`
    color: #757575;
    width: 16px;
    height: 16px;
    cursor: grab;
`;

const StarIcon = styled(Star) <{ favorite: boolean }>`
    color: ${props => (props.favorite ? '#325CD6' : '#BDBDBD')};
    width: 16px;
    height: 16px;
`;

const MoreIcon = styled(MoreVertical)`
    color: #757575;
    width: 16px;
    height: 16px;
`;

const AssignedProfiles = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const OverlappingImageWrapper = styled.div`
    display: inline-block;
    position: relative;
    margin-left: -8px; 
    &:first-of-type {
        margin-left: 0; 
    }
`;

const OverlappingImages = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start; 
`;

const CandidateImage = styled(Avatar) <{ background: string }>`
    width: 16px;
    height: 16px;
    background-color: ${props => props.background + "!important"};
    color: #171717 !important;
    font-weight: 600;
    opacity: 0.7;
    border: 2px solid white;
`;

const IncompleteTasksSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: #757575;
    border-top: 1px solid #E0E0E0;
    padding-top: 8px;
`;

const JobCard = forwardRef<HTMLDivElement, JobCardProps>(({ candidate, index, onMenuAction, onFavoriteToggle }, ref) => {
    const { expertId } = useSelector((state: any) => state.auth.user);
    const { currentJob, loadJobs, handleActiveCandidateProfile } = useJobPipeline();
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [addCommentPopover, setAddCommentPopover] = useState<null | HTMLElement>(null);
    const [loading, setLoading] = useState(false);

    const getInitials = (name: string) => {
        const nameParts = name.split(' ');
        const initials = nameParts.map(part => part[0]).join('');
        return initials.toUpperCase();
    };

    const handleFavoriteClick = async () => {
        setLoading(true);
        try {
            await toggleFavoriteCandidate(
                currentJob?.expertId || '',
                currentJob?.jobId || '',
                candidate.candidateId,
                !candidate.favorite
            );
            onFavoriteToggle(candidate.candidateId, !candidate.favorite);
        } catch (error) {
            console.error('Failed to toggle favorite status:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCandidateProfile = (candidate: Candidate) => {
        handleActiveCandidateProfile(candidate);
    };

    const handleChatClick = (event: React.MouseEvent<HTMLElement>) => {
        setAddCommentPopover(event.currentTarget);
    };

    const incompleteTasks = candidate.candidateTaskDetails?.filter(
        (task) => task.status !== "TASK_COMPLETED"
    );

    const latestCompletedTask = candidate.candidateTaskDetails
        ?.filter((task) => task.status === "TASK_COMPLETED")
        ?.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];

    const handlePopoverClose = () => {
        setAddCommentPopover(null);
        if (currentJob) {
            loadJobs(currentJob.jobId);
        }
    };

    const convertMessage = (message: any) => {
        if (!message) return 'No tasks';
        const regex = /@\[(.+?)\]\([^\)]+\)/g;
        return message.replace(regex, '@$1');
      };



    return (
        <Draggable draggableId={candidate.candidateId} index={index}>
            {(provided) => (
                <CardContainer
                    ref={ref}
                    {...provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}

                >
                    {candidate.meetingDetails && candidate.meetingDetails.meetingDetailId && 
                        <ScheduledTag>Meeting Scheduled</ScheduledTag> || null}
                    <CandidateHeader>
                        <DragIcon />
                        <StatsSection>
                            {!!candidate.candidateResumeDetails?.companyTire && (
                                <>
                                    <span className="tier">Tier {candidate.candidateResumeDetails.companyTire}</span> |
                                </>
                            )}
                            {!!candidate.candidateEvaluationDetails?.aiEvaluationScore && (
                                <>
                                    <span className="ai">AI: {candidate.candidateEvaluationDetails.aiEvaluationScore}</span> |
                                </>
                            )}
                            {!!candidate.candidateEvaluationDetails?.subjectMatterExpertEvaluationScore && (
                                <>
                                    <span className="sme">SME: {candidate.candidateEvaluationDetails.subjectMatterExpertEvaluationScore}</span> |
                                </>
                            )}
                            {!!candidate.candidateEvaluationDetails?.coachEvaluationScore && (
                                <>
                                    <span className="sme">COACH: {candidate.candidateEvaluationDetails.coachEvaluationScore}</span> |
                                </>
                            )}
                            {!!candidate.candidateResumeDetails?.universityScore && (
                                <>
                                    <span className="sme">{candidate.candidateResumeDetails.universityScore}</span> |
                                </>
                            )}
                            {!!candidate.candidateResumeDetails?.jobMatchScore && (
                                <>
                                    <span className="score">{candidate.candidateResumeDetails.jobMatchScore}%</span> |
                                </>
                            )}

                            

                            <IconButton onClick={handleFavoriteClick} size="small" disabled={loading}>
                                {loading ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <StarIcon favorite={!!candidate.favorite} />
                                )}
                            </IconButton>
                            <IconButton onClick={(e) => setMenuAnchorEl(e.currentTarget)} size="small">
                                <MoreIcon color='#325CD6' />
                            </IconButton>
                        </StatsSection>
                    </CandidateHeader>

                    <CandidateHeader>
                        <CandidateNameSection>
                            <CandidateImage
                                src={candidate.image || ''}
                                background={candidate.image ? '' : '#BFD0FF'}
                            >
                                {!candidate.image && getInitials(candidate.fullname)}
                            </CandidateImage>
                            <CandidateName>
                                <span>{candidate.fullname}</span>
                                <LastUpdated>Last updated: {moment(candidate.lastActivityAt).format('MMMM D, YYYY')}</LastUpdated>
                            </CandidateName>
                        </CandidateNameSection>
                    </CandidateHeader>

                    <CandidateTags>
                        {candidate.candidateStates.map((state, idx) => (
                            <Tag key={idx} background={!state.completed ? "#f57c00" : "#4caf50"}>{formatLabel(state.label)}</Tag>
                        ))}
                        
                    </CandidateTags>

                    <IncompleteTasksSection>
                        <AssignedProfiles>
                            <OverlappingImages>
                                {incompleteTasks && incompleteTasks.length > 0 ? (
                                    <>
                                        {incompleteTasks.map((profile, index) => (
                                            <OverlappingImageWrapper key={index}>
                                                <CandidateImage
                                                    src={profile.expertInfo.imgUrl || ''}
                                                    background={'#2979ff7a'}
                                                    css={css`
                                                        width: 16px;
                                                        height: 16px;
                                                    `}
                                                >
                                                    {!profile.expertInfo.imgUrl && getInitials(profile.expertInfo.fullName)}
                                                </CandidateImage>
                                            </OverlappingImageWrapper>
                                        ))}
                                        <span>Incomplete tasks: {incompleteTasks.length}</span>
                                    </>
                                ) : (
                                    <>
                                        {latestCompletedTask && (
                                            <OverlappingImageWrapper>
                                                <CandidateImage
                                                    src={latestCompletedTask.expertInfo.imgUrl || ''}
                                                    background={'#2979ff7a'}
                                                    css={css`
                                                        width: 16px;
                                                        height: 16px;
                                                    `}
                                                >
                                                    {!latestCompletedTask.expertInfo.imgUrl && getInitials(latestCompletedTask.expertInfo.fullName)}
                                                </CandidateImage>
                                            </OverlappingImageWrapper>
                                        )}
                                        <span
                                         style={{wordBreak:"break-word", width:"100%"}}
                                            >
                                                {convertMessage(latestCompletedTask?.message)?.length > 153
                                                ? `${convertMessage(latestCompletedTask?.message).slice(0, 150)}...`
                                                : convertMessage(latestCompletedTask?.message)}
                                            </span>
                                    </>
                                )}
                            </OverlappingImages>
                        </AssignedProfiles>
                        <div style={{display:"flex", alignItems:"center"}}>
                            <IconButton size="small" onClick={handleChatClick}>
                                <ChatDotsFill color='#325CD6' size="16" />
                            </IconButton>
                            |
                            <IconButton size="small" onClick={() => handleCandidateProfile(candidate)}>
                                <Person color='#325CD6' size="16" />
                            </IconButton>
                        </div>
                    </IncompleteTasksSection>

                    <JobCardMenuOptions
                        anchorEl={menuAnchorEl}
                        onClose={() => setMenuAnchorEl(null)}
                        candidateId={candidate.candidateId}
                        candidateFullName={candidate.fullname}
                        candidateTrackId={candidate.candidateTrackId || ''}
                        trackId={currentJob?.trackId || ''}
                        expertId={currentJob?.expertId || ''}
                        candidate={candidate}
                    />

                    <Popover
                        open={Boolean(addCommentPopover)}
                        anchorEl={addCommentPopover}
                        onClose={handlePopoverClose}
                        anchorOrigin={{
                            vertical: "center",
                            horizontal: 'left',
                        }}
                    >
                        <AddCommentModal
                            expertId={expertId}
                            jobId={currentJob?.jobId || ''}
                            ownerExpert={{ fullname: currentJob?.expertInfo?.fullName || "", expertId: currentJob?.expertInfo?.expertId || "", email: currentJob?.expertInfo?.email || "" }}
                            candidateId={candidate.candidateId}
                        />
                    </Popover>
                </CardContainer>
            )}
        </Draggable>
    );
});

export default JobCard;